import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { listFiles, deleteFile } from '../services/api';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import dayjs from 'dayjs';

const PAGE_SIZE = 20;

function FileList() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [currentUserAlias, setCurrentUserAlias] = useState('');
  const [page, setPage] = useState(1);
  const [nextKey, setNextKey] = useState(null);
  const cursorStack = useRef([null]);

  const loadPage = useCallback(async (cursor) => {
    setLoading(true);
    try {
      const data = await listFiles(PAGE_SIZE, cursor);
      setFiles(data.files || []);
      setNextKey(data.lastKey || null);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(null); }, [loadPage]);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email || '';
        const user = await getCurrentUser();
        const raw = email || user.username || '';
        setCurrentUserAlias(raw.includes('@') ? raw.split('@')[0] : raw);
      } catch { /* not signed in */ }
    })();
  }, []);

  const goNext = () => {
    if (!nextKey) return;
    const newPage = page + 1;
    if (cursorStack.current.length < newPage) {
      cursorStack.current.push(nextKey);
    }
    setPage(newPage);
    loadPage(nextKey);
  };

  const goPrev = () => {
    if (page <= 1) return;
    const newPage = page - 1;
    setPage(newPage);
    loadPage(cursorStack.current[newPage - 1]);
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      await deleteFile(fileId);
      setFiles(prev => prev.filter(f => f.fileId !== fileId));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const filteredFiles = files.filter(f => {
    if (filter !== 'all' && f.status !== filter) return false;
    if (ownerFilter === 'mine' && currentUserAlias && f.uploadedBy?.toLowerCase() !== currentUserAlias.toLowerCase()) return false;
    return true;
  });

  const statusBadge = (status) => {
    const colors = {
      'Pending Review': 'badge-warning',
      'Approved': 'badge-success',
      'Rejected': 'badge-danger',
      'In Review': 'badge-info'
    };
    return <span className={`badge ${colors[status] || 'badge-default'}`}>{status}</span>;
  };

  if (loading && files.length === 0) return <div className="loading">Loading files...</div>;

  return (
    <div className="file-list-container">
      <div className="list-header">
        <h2>TestGenius Dashboard</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="filter-bar">
            {['all', 'mine'].map(f => (
              <button
                key={f}
                className={`filter-btn ${ownerFilter === f ? 'active' : ''}`}
                onClick={() => setOwnerFilter(f)}
              >
                {f === 'all' ? '👥 All' : '👤 My Files'}
              </button>
            ))}
          </div>
          <div className="filter-bar">
            {['all', 'Pending Review', 'In Review', 'Approved', 'Rejected'].map(f => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filteredFiles.length === 0 ? (
        <div className="empty-state">
          <p>No files found. <Link to="/upload">Upload a CSV file</Link> to get started.</p>
        </div>
      ) : (
        <div className="file-table-wrapper">
          <table className="file-table" role="table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Uploaded By</th>
                <th>Date</th>
                <th>Status</th>
                <th>Approvals</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map(file => (
                <tr key={file.fileId}>
                  <td className="file-name-cell">
                    <Link to={`/review/${file.fileId}`} style={{ textDecoration: 'none', color: 'var(--primary)' }}>
                      📄 {file.fileName}
                    </Link>
                  </td>
                  <td>{file.uploadedBy}</td>
                  <td>{dayjs(file.createdAt).format('MMM D, YYYY h:mm A')}</td>
                  <td>{statusBadge(file.status)}</td>
                  <td>{file.approvedCount}/{file.totalApprovers}</td>
                  <td>
                    <Link to={`/review/${file.fileId}`} className="btn btn-primary btn-sm">Review</Link>
                    {file.status !== 'Approved' && (
                      <button
                        onClick={() => handleDelete(file.fileId, file.fileName)}
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: '0.5rem' }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination-controls">
        <button onClick={goPrev} disabled={page <= 1} className="btn btn-secondary btn-sm">← Previous</button>
        <span className="page-indicator">Page {page}</span>
        <button onClick={goNext} disabled={!nextKey} className="btn btn-secondary btn-sm">Next →</button>
      </div>
    </div>
  );
}

export default FileList;
